import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertTriangle, Search, FileX2, RotateCcw, ChevronLeft, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_TYPES = [
  { v: 'FC', label: 'Factura (FC)' },
  { v: 'ND', label: 'Nota de Débito (ND)' },
];
const TALONARIOS = [
  { v: 'proforma', label: 'Proforma' },
  { v: 'arca', label: 'ARCA (fiscal)' },
];
const TARGETS = [
  { v: 'resident', label: 'Residente' },
  { v: 'responsible', label: 'Responsable' },
  { v: 'insurer', label: 'Convenio (vía residente)' },
  { v: 'insurer_direct', label: 'Convenio directo' },
];

function userCan(user, mod, act) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[act] === true;
}

export default function BatchCreditNote() {
  const { user } = useOutletContext();
  const canEdit = userCan(user, 'residentes', 'editar');

  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';

  const [filters, setFilters] = useState({
    date_from: firstOfMonth,
    date_to: today,
    document_types: ['FC', 'ND'],
    talonarios: ['proforma'],
    targets: [],
    invoice_number_like: '',
    service_ids: [],
  });
  const [services, setServices] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // {invoice_id: true}
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [ncReason, setNcReason] = useState('NC masiva — reversión');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const loadServices = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/resident-services`);
      setServices(r.data || []);
    } catch (e) {
      // silently
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const toggleArr = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const loadCandidates = async () => {
    setLoading(true);
    setResult(null);
    try {
      const body = {
        ...filters,
        service_ids: serviceFilter && serviceFilter !== 'all' ? [serviceFilter] : null,
        document_types: filters.document_types.length ? filters.document_types : null,
        talonarios: filters.talonarios.length ? filters.talonarios : null,
        targets: filters.targets.length ? filters.targets : null,
      };
      const r = await axios.post(`${BACKEND_URL}/api/invoices/batch-credit-note/candidates`, body);
      setRows(r.data.rows || []);
      // auto-seleccionar los elegibles
      const sel = {};
      (r.data.rows || []).forEach((row) => {
        if (row.eligible) sel[row.invoice_id] = true;
      });
      setSelected(sel);
      toast.success(`${r.data.count} comprobantes encontrados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar comprobantes');
    } finally {
      setLoading(false);
    }
  };

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const selectedRows = rows.filter((r) => selected[r.invoice_id]);
  const selectedTotal = selectedRows.reduce((a, r) => a + (r.creditable || 0), 0);
  const selectedArca = selectedRows.filter((r) => r.talonario === 'arca').length;

  const toggleAll = () => {
    if (selectedIds.length === rows.filter((r) => r.eligible).length) {
      setSelected({});
    } else {
      const sel = {};
      rows.forEach((r) => {
        if (r.eligible) sel[r.invoice_id] = true;
      });
      setSelected(sel);
    }
  };

  const openPreview = async () => {
    if (!selectedIds.length) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/invoices/batch-credit-note/preview`, {
        invoice_ids: selectedIds,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en preview');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyBatch = async () => {
    if (!selectedIds.length) return;
    const arcaCount = selectedArca;
    const warn = arcaCount > 0
      ? `Vas a emitir ${selectedIds.length} NC (${arcaCount} se enviarán a AFIP). ¿Confirmás?`
      : `Vas a emitir ${selectedIds.length} NC proforma. ¿Confirmás?`;
    if (!window.confirm(warn)) return;
    setApplying(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/invoices/batch-credit-note/apply`, {
        invoice_ids: selectedIds,
        nc_reason: ncReason || 'NC masiva — reversión',
        continue_on_error: true,
      });
      setResult(r.data);
      setPreviewOpen(false);
      if (r.data.fail_count === 0) {
        toast.success(`${r.data.ok_count} NC emitidas con éxito`);
      } else {
        toast.warning(`${r.data.ok_count} OK / ${r.data.fail_count} fallaron`);
      }
      // Refrescar lista
      loadCandidates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al emitir NC masivas');
    } finally {
      setApplying(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="p-6">
        <p className="text-slate-600">No tenés permisos para emitir NC masivas.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="batch-nc-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Link to="/billing" className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Volver a Facturación
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-2 flex items-center gap-2">
            <FileX2 className="w-7 h-7 text-rose-600" />
            NC Masiva — Reversión en lote
          </h1>
          <p className="text-slate-600 text-sm mt-1 max-w-3xl">
            Herramienta para revertir en bloque facturas (FC) o notas de débito (ND) emitidas por error.
            Para cada comprobante seleccionado se emite una Nota de Crédito completa que mantiene la trazabilidad
            fiscal: si el origen es ARCA, la NC también se transmite a AFIP.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Search className="w-4 h-4" /> Filtros
          </h3>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Fecha desde</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Fecha hasta</Label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              data-testid="filter-date-to"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">N° comprobante contiene</Label>
            <Input
              placeholder="Ej: ND-B-00004"
              value={filters.invoice_number_like}
              onChange={(e) => setFilters({ ...filters, invoice_number_like: e.target.value })}
              data-testid="filter-number"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Servicio / Concepto</Label>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger data-testid="filter-service"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los conceptos</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.service_id} value={s.service_id}>
                    {s.code ? `[${s.code}] ` : ''}{s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">Tipo de documento</Label>
            <div className="flex gap-3 pt-2 flex-wrap">
              {DOC_TYPES.map((d) => (
                <label key={d.v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={filters.document_types.includes(d.v)}
                    onCheckedChange={() =>
                      setFilters({ ...filters, document_types: toggleArr(filters.document_types, d.v) })
                    }
                    data-testid={`filter-doc-${d.v}`}
                  />
                  {d.label}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">Talonario</Label>
            <div className="flex gap-3 pt-2 flex-wrap">
              {TALONARIOS.map((t) => (
                <label key={t.v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={filters.talonarios.includes(t.v)}
                    onCheckedChange={() =>
                      setFilters({ ...filters, talonarios: toggleArr(filters.talonarios, t.v) })
                    }
                    data-testid={`filter-tal-${t.v}`}
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs text-slate-600">Destinatario</Label>
            <div className="flex gap-3 pt-2 flex-wrap">
              {TARGETS.map((t) => (
                <label key={t.v} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={filters.targets.includes(t.v)}
                    onCheckedChange={() =>
                      setFilters({ ...filters, targets: toggleArr(filters.targets, t.v) })
                    }
                    data-testid={`filter-target-${t.v}`}
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button
              onClick={loadCandidates}
              disabled={loading}
              className="bg-slate-800 hover:bg-slate-900"
              data-testid="btn-search-candidates"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Buscar comprobantes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      {rows.length > 0 && (
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-slate-800">Comprobantes candidatos ({rows.length})</h3>
              <p className="text-xs text-slate-500">
                Seleccionados: <b>{selectedIds.length}</b> · Total a acreditar: <b>{fmt(selectedTotal)}</b>
                {selectedArca > 0 && <span className="text-rose-600"> · {selectedArca} irán a AFIP</span>}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={toggleAll} data-testid="btn-select-all">
                {selectedIds.length === rows.filter((r) => r.eligible).length ? 'Deseleccionar todos' : 'Seleccionar elegibles'}
              </Button>
              <Button
                onClick={openPreview}
                disabled={!selectedIds.length}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="btn-preview"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Previsualizar impacto
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Nº</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Talonario</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Cobrado</TableHead>
                  <TableHead className="text-right">Acreditable</TableHead>
                  <TableHead>CAE</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.invoice_id}
                    className={!r.eligible ? 'opacity-40' : ''}
                    data-testid={`row-${r.invoice_id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={!!selected[r.invoice_id]}
                        disabled={!r.eligible}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.invoice_id]: !!v }))}
                        data-testid={`chk-${r.invoice_id}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.issue_date)}</TableCell>
                    <TableCell className="text-xs">
                      <div>{r.billed_to_name}</div>
                      {r.target === 'insurer_direct' && <span className="text-sky-600 text-[10px]">Convenio directo</span>}
                      {r.target === 'insurer' && <span className="text-sky-600 text-[10px]">A convenio del residente</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{r.document_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={r.talonario === 'arca' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'}>
                        {r.talonario}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs">{fmt(r.total)}</TableCell>
                    <TableCell className="text-right text-xs">{fmt(r.paid_amount)}</TableCell>
                    <TableCell className="text-right text-xs font-semibold">{fmt(r.creditable)}</TableCell>
                    <TableCell className="text-xs font-mono">{r.cae || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Resultado */}
      {result && (
        <Card className="border-emerald-200">
          <CardHeader>
            <h3 className="font-semibold text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Resultado del lote
            </h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-3 bg-emerald-50 rounded">
                <div className="text-xs text-slate-600">NC emitidas</div>
                <div className="text-2xl font-bold text-emerald-700">{result.ok_count}</div>
              </div>
              <div className="p-3 bg-rose-50 rounded">
                <div className="text-xs text-slate-600">Fallidas</div>
                <div className="text-2xl font-bold text-rose-700">{result.fail_count}</div>
              </div>
              <div className="p-3 bg-slate-50 rounded">
                <div className="text-xs text-slate-600">Total acreditado</div>
                <div className="text-2xl font-bold text-slate-800">{fmt(result.total_credited)}</div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Factura origen</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>NC emitida</TableHead>
                  <TableHead>CAE</TableHead>
                  <TableHead>Detalle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.results.map((r, i) => (
                  <TableRow key={i} data-testid={`result-${i}`}>
                    <TableCell className="font-mono text-xs">{r.source_invoice_number || r.invoice_id}</TableCell>
                    <TableCell>
                      {r.ok ? (
                        <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> OK</span>
                      ) : (
                        <span className="text-rose-700 flex items-center gap-1"><XCircle className="w-4 h-4" /> Error</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.nc_invoice_number || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{r.nc_cae || '—'}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.error || `${fmt(r.nc_total || 0)} · ${r.nc_talonario || ''}`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog Preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Previsualización de impacto
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Calculando impacto…
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded">
                  <div className="text-xs text-slate-600">Elegibles</div>
                  <div className="text-2xl font-bold text-emerald-700">{preview.eligible_count}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded">
                  <div className="text-xs text-slate-600">No elegibles</div>
                  <div className="text-2xl font-bold text-slate-700">{preview.ineligible_count}</div>
                </div>
                <div className="p-3 bg-amber-50 rounded">
                  <div className="text-xs text-slate-600">Total a acreditar</div>
                  <div className="text-xl font-bold text-amber-800">{fmt(preview.total_creditable)}</div>
                </div>
              </div>

              {preview.arca_count > 0 && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Atención: <b>{preview.arca_count}</b> NC se emitirán a AFIP (ARCA real).
                  {preview.proforma_count > 0 && <span> Las otras <b>{preview.proforma_count}</b> son proforma internas.</span>}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Por talonario</div>
                  {Object.entries(preview.by_talonario || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1 border-b">
                      <span>{k}</span>
                      <span>{v.count} · {fmt(v.amount)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Por destinatario</div>
                  {Object.entries(preview.by_target || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1 border-b">
                      <span>{k}</span>
                      <span>{v.count} · {fmt(v.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600">Motivo (quedará registrado en cada NC)</Label>
                <Textarea
                  value={ncReason}
                  onChange={(e) => setNcReason(e.target.value)}
                  rows={2}
                  data-testid="input-nc-reason"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cancelar</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700"
              onClick={applyBatch}
              disabled={applying || !preview || preview.eligible_count === 0}
              data-testid="btn-apply"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileX2 className="w-4 h-4 mr-2" />}
              Emitir {preview?.eligible_count || 0} NC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
